<script lang="ts">
  import { slide } from "svelte/transition";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { createEventDispatcher } from "svelte";
  import { flip } from "svelte/animate";
  import Picture from "../Picture/Picture.svelte";

  export let title: string;
  export let subtitle = "";
  export let mdImage = "";
  export let smImage = mdImage;
  export let border = false;
  export let small = false;
  export let innerBorder = true;
  export let scrollable = false;
  export let index: number = 0;
  export let light = false;
  export let footerLinks = false;
  export let caret = false;
  export let hideOnceOpened = false;
  export let disabled = false;
  export let invert = false;

  const id = title.replace(/\s+/g, "-").toLowerCase();

  export let scroll: any = null;
  export let accordionOpen: boolean = false;

  const dispatch = createEventDispatcher();

  const toggleAccordion = () => {
    dispatch("accordionToggle", index);
    accordionOpen = !accordionOpen;
    if (scroll && accordionOpen === true) {
      scroll.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
</script>

<style lang="scss">
  .accordion-item {
    padding: 16px $inlineIconLeftSm;

    @include breakpoint("md") {
      padding: 16px $inlineIconLeftMd;
    }
    @include breakpoint("lg") {
      padding: 16px $inlineIconLeftLg;
    }
    @include breakpoint("xl") {
      padding: 16px $inlineIconLeftXl;
    }
    @include breakpoint("xxl") {
      padding: 16px $inlineIconLeftXxl;
    }

    &--header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      text-decoration: none;

      &.open {
        border-top: 1px solid $on-surface-10;
        padding-bottom: 16px;
      }

      &.open--light {
        border-top: 1px solid $inverse-on-surface-60;
        padding-bottom: 16px;
      }

      &--picture {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        margin-top: 16px;
        height: 344px;

        @include breakpoint(md) {
          height: 480px;
        }
      }
    }

    &--inner {
      padding: 16px $inlineIconLeftSm;

      @include breakpoint("md") {
        padding: 16px $inlineIconLeftMd;
      }
      @include breakpoint("lg") {
        padding: 16px $inlineIconLeftLg;
      }
      @include breakpoint("xl") {
        padding: 16px $inlineIconLeftXl;
      }
      @include breakpoint("xxl") {
        padding: 16px $inlineIconLeftXxl;
      }
      &__bottom-border {
        border-bottom: 1px solid $on-surface-10;
        &__light {
          border-bottom: 1px solid $inverse-on-surface-60;
        }
      }

      @include breakpoint(lg) {
        padding-bottom: 8px;
      }

      &__hidden {
        visibility: hidden;
        display: none;
        padding: 0;
        height: 0;
      }
    }

    &--title {
      display: flex;
      flex-direction: column-reverse;
    }
  }
  .border {
    border-top: 1px solid $on-surface-10;
    transition-delay: 0.3s;
  }

  .border__light {
    border-top: 1px solid $background;
    transition-delay: 0.3s;
  }
  .slot {
    scroll-margin-top: 72px;

    @include breakpoint(lg) {
      scroll-margin-top: 116px;
    }
  }

  .invert {
    background-color: $inverse-surface;
    color: $background;

    &--border {
      border-top: 1px solid $background;
    }
  }

  :global(.accordion-item--header--picture > picture) {
    width: 100%;
  }
</style>

{#if !hideOnceOpened || !accordionOpen}
  <div
    class={`accordion-item${hideOnceOpened && accordionOpen ? "" : ""}  ${
      !light && ((!innerBorder && accordionOpen) || !border)
        ? border
          ? light
            ? "border__light"
            : "border"
          : ""
        : footerLinks
          ? ""
          : light
            ? border
              ? "border__light"
              : ""
            : "border"
    } ${invert ? "invert" : ""}`}
  >
    {#if scrollable}
      <div class="slot" bind:this={scroll} />
    {/if}

    <div
      class={`accordion-item--header ${
        !light && !border && accordionOpen && innerBorder
          ? "open"
          : light && !border && accordionOpen && innerBorder
            ? "open--light"
            : ""
      }`}
      role="button"
      tabindex="0"
      on:click={!disabled && toggleAccordion}
      on:keydown={!disabled && toggleAccordion}
    >
      <div class="accordion-item--title">
        <Typography
          component="h3"
          variant={small ? "h3-2" : subtitle ? "h1" : "button1"}
          theme={light ? "inverse-on-surface" : "on-background"}
        >
          {@html title}
        </Typography>
        {#if subtitle}
          <Typography
            component="h4"
            variant="body1"
            theme={light ? "inverse-on-surface" : "on-background"}
          >
            {subtitle}
          </Typography>
        {/if}
      </div>
      {#if !disabled}
        {#if caret}
          <div
            class="accordion-item--icon"
            style:transform={`rotate(${accordionOpen ? 180 : 0}deg)`}
            style:transition="transform 0.3s"
          >
            <Icon
              icon={"keyboard_arrow_down"}
              color={light
                ? "var(--inverse-on-surface)"
                : "var(--on-background)"}
            />
          </div>
        {:else}
          <div class="accordion-item--icon">
            <Icon
              icon={accordionOpen ? "remove" : "add"}
              color={light
                ? "var(--inverse-on-surface)"
                : "var(--on-background)"}
            />
          </div>
        {/if}
      {/if}
    </div>
    {#if mdImage}
      <div class="accordion-item--header--picture">
        <Picture {mdImage} {smImage} altText={title} fitOrFill="fill" />
      </div>
    {/if}
  </div>
{/if}
{#if accordionOpen}
  <div
    class={`accordion-item--inner ${invert ? "invert" : ""} ${
      innerBorder ? (light ? "border__light" : "border") : ""
    }`}
    transition:slide
  >
    <slot />
  </div>
{:else}
  <div class="accordion-item--inner accordion-item--inner__hidden">
    <slot />
  </div>
{/if}
